import React, { useState, useEffect, useCallback } from 'react';

function Marketplace() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [anuncios, setAnuncios] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [perguntas, setPerguntas] = useState([]);
  const [resumoVendas, setResumoVendas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filtroAnuncio, setFiltroAnuncio] = useState('active');
  const [buscaAnuncio, setBuscaAnuncio] = useState('');
  const [modalAnuncio, setModalAnuncio] = useState(null);
  const [modalResposta, setModalResposta] = useState(null);
  const [resposta, setResposta] = useState('');
  const [modalCriar, setModalCriar] = useState(false);
  const [novoAnuncio, setNovoAnuncio] = useState({titulo:'',categoria_id:'',categoria_nome:'',preco:'',estoque:'1',condicao:'new',tipo_publicacao:'gold_special',descricao:'',fotos:[''],atributos:[]});
  const [categoriaSugestoes, setCategoriaSugestoes] = useState([]);
  const [atributosCategoria, setAtributosCategoria] = useState([]);
  const [criando, setCriando] = useState(false);
  const [buscandoCat, setBuscandoCat] = useState(false);
  const [fotosUpload, setFotosUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mesVendas, setMesVendas] = useState(new Date().toISOString().slice(0,7));

  const fmt = (v) => new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(v||0);

  const carregarDashboard = useCallback(async () => {
    try {
      const [d,r] = await Promise.all([
        fetch('/api/mercadolivre/dashboard?mes='+mesVendas).then(r=>r.json()),
        fetch('/api/mercadolivre/vendas/resumo?mes='+mesVendas).then(r=>r.json())
      ]);
      if(d.success) setDashboard(d.dashboard);
      if(r.success) setResumoVendas(r.resumo);
    } catch(e) { console.error(e); }
  }, [mesVendas]);

  const carregarAnuncios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if(filtroAnuncio!=='all') params.set('status',filtroAnuncio);
      if(buscaAnuncio) params.set('busca',buscaAnuncio);
      const r = await fetch('/api/mercadolivre/anuncios?'+params).then(r=>r.json());
      if(r.success) setAnuncios(r.anuncios);
    } catch(e) { console.error(e); }
  }, [filtroAnuncio, buscaAnuncio]);

  const carregarVendas = useCallback(async () => {
    try {
      const r = await fetch('/api/mercadolivre/vendas?mes='+mesVendas).then(r=>r.json());
      if(r.success) setVendas(r.vendas);
    } catch(e) { console.error(e); }
  }, [mesVendas]);

  const carregarPerguntas = useCallback(async () => {
    try {
      const r = await fetch('/api/mercadolivre/perguntas').then(r=>r.json());
      if(r.success) setPerguntas(r.perguntas);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([carregarDashboard(), carregarAnuncios(), carregarVendas(), carregarPerguntas()]);
      setLoading(false);
    };
    load();
  }, [carregarDashboard, carregarAnuncios, carregarVendas, carregarPerguntas]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      await fetch('/api/mercadolivre/anuncios/sync');
      await fetch('/api/mercadolivre/vendas/sync');
      await fetch('/api/mercadolivre/perguntas/sync');
      await Promise.all([carregarDashboard(), carregarAnuncios(), carregarVendas(), carregarPerguntas()]);
    } catch(e) { console.error(e); }
    setSyncing(false);
  };

  const pausarAnuncio = async (id) => {
    await fetch('/api/mercadolivre/anuncios/'+id+'/pausar',{method:'PUT'});
    carregarAnuncios();
  };
  const reativarAnuncio = async (id) => {
    await fetch('/api/mercadolivre/anuncios/'+id+'/reativar',{method:'PUT'});
    carregarAnuncios();
  };
  const responderPergunta = async (id) => {
    if(!resposta.trim()) return;
    await fetch('/api/mercadolivre/perguntas/'+id+'/responder',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({resposta})});
    setModalResposta(null);
    setResposta('');
    carregarPerguntas();
  };

  const buscarCategorias = async (titulo) => {
    if(titulo.length<3) return;
    setBuscandoCat(true);
    try {
      const r = await fetch('/api/mercadolivre/categorias/predizer/'+encodeURIComponent(titulo)).then(r=>r.json());
      if(r.success) setCategoriaSugestoes(r.categorias||[]);
    } catch(e) { console.error(e); }
    setBuscandoCat(false);
  };

  const selecionarCategoria = async (cat) => {
    setNovoAnuncio(p=>({...p,categoria_id:cat.category_id,categoria_nome:cat.category_name||cat.domain_name}));
    setCategoriaSugestoes([]);
    try {
      const r = await fetch('/api/mercadolivre/categorias/'+cat.category_id+'/atributos').then(r=>r.json());
      if(r.success) {
        const obr = (r.obrigatorios||[]).map(a=>({id:a.id,name:a.name,value_name:'',values:a.values||[]}));
        setAtributosCategoria(obr);
        setNovoAnuncio(p=>({...p,atributos:obr.map(a=>({id:a.id,value_name:''}))}));
      }
    } catch(e) { console.error(e); }
  };

  const uploadFoto = async (file) => {
    if(!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      const r = await fetch('/api/mercadolivre/upload-imagem',{method:'POST',body:fd}).then(r=>r.json());
      if(r.success) {
        setFotosUpload(p=>[...p,{id:r.picture_id,url:r.url,name:file.name}]);
        setNovoAnuncio(p=>({...p,fotos:[...p.fotos.filter(f=>f.trim()),r.url]}));
      } else {
        alert('Erro upload: '+(r.error?.message||JSON.stringify(r.error)));
      }
    } catch(e) { alert('Erro upload: '+e.message); }
    setUploading(false);
  };

  const removerFoto = (idx) => {
    setFotosUpload(p=>p.filter((_,i)=>i!==idx));
    const novasFotos = fotosUpload.filter((_,i)=>i!==idx).map(f=>f.url);
    setNovoAnuncio(p=>({...p,fotos:novasFotos}));
  };

  const atualizarFoto = (idx,val) => {
    const f = [...novoAnuncio.fotos];
    f[idx] = val;
    setNovoAnuncio(p=>({...p,fotos:f}));
  };

  const addFoto = () => setNovoAnuncio(p=>({...p,fotos:[...p.fotos,'']}));

  const atualizarAtributo = (idx,val) => {
    const a = [...novoAnuncio.atributos];
    a[idx] = {...a[idx],value_name:val};
    setNovoAnuncio(p=>({...p,atributos:a}));
  };

  const criarAnuncio = async () => {
    if(!novoAnuncio.titulo||!novoAnuncio.categoria_id||!novoAnuncio.preco) return alert('Preencha titulo, categoria e preco');
    setCriando(true);
    try {
      const body = {
        titulo:novoAnuncio.titulo,
        categoria_id:novoAnuncio.categoria_id,
        preco:novoAnuncio.preco,
        estoque:novoAnuncio.estoque,
        condicao:novoAnuncio.condicao,
        tipo_publicacao:novoAnuncio.tipo_publicacao,
        descricao:novoAnuncio.descricao,
        fotos:novoAnuncio.fotos.filter(f=>f.trim()),
        atributos:novoAnuncio.atributos.filter(a=>a.value_name)
      };
      const r = await fetch('/api/mercadolivre/anuncios/criar',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(r=>r.json());
      if(r.success) {
        alert('Anuncio criado com sucesso! ID: '+r.item.id);
        setModalCriar(false);
        setNovoAnuncio({titulo:'',categoria_id:'',categoria_nome:'',preco:'',estoque:'1',condicao:'new',tipo_publicacao:'gold_special',descricao:'',fotos:[''],atributos:[]});
        setAtributosCategoria([]);
        setFotosUpload([]);
        carregarAnuncios();
        carregarDashboard();
      } else {
        const msg = (r.detalhes && r.detalhes.length > 0) ? r.detalhes.join(', ') : (r.error?.message || r.error?.cause?.[0]?.message || JSON.stringify(r.error));
        alert('Erro ao criar: '+msg);
      }
    } catch(e) { alert('Erro: '+e.message); }
    setCriando(false);
  };

  const d = dashboard || {anuncios:{total:0,ativos:0,pausados:0},vendas:{total:0,faturamento:0,taxas:0,frete:0},perguntas:{total:0,pendentes:0}};
  const rv = resumoVendas || {total_vendas:0,faturamento:0,total_taxas:0,ticket_medio:0};

  if(loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>Carregando Marketplace...</div>;

  return (
    <div style={{padding:'24px 28px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:'var(--text-primary)',margin:0}}>Marketplace Mercado Livre</h1>
          <p style={{fontSize:13,color:'var(--text-muted)',marginTop:4}}>GRUPOGVS - Gerenciamento de anuncios e vendas</p>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <input type="month" value={mesVendas} onChange={e=>setMesVendas(e.target.value)} style={{padding:'7px 12px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:13}} />
          <button onClick={syncAll} disabled={syncing} style={{padding:'8px 16px',background:'var(--accent)',color:'white',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',opacity:syncing?0.6:1}}>
            {syncing?'Sincronizando...':'Sincronizar ML'}
          </button>
        </div>
      </div>

      <div style={{display:'flex',gap:4,marginBottom:20,background:'var(--bg-card)',padding:4,borderRadius:10,border:'1px solid var(--border)'}}>
        {[['dashboard','Dashboard'],['anuncios','Anuncios'],['vendas','Vendas'],['perguntas','Perguntas']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{flex:1,padding:'10px 16px',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',background:tab===k?'var(--accent)':'transparent',color:tab===k?'white':'var(--text-secondary)',transition:'all .2s'}}>
            {l} {k==='perguntas'&&parseInt(d.perguntas.pendentes)>0&&<span style={{background:'var(--red)',color:'white',borderRadius:99,padding:'2px 7px',fontSize:11,marginLeft:6}}>{d.perguntas.pendentes}</span>}
          </button>
        ))}
      </div>

      {tab==='dashboard'&&(
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,borderTop:'3px solid var(--accent)'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5}}>Anuncios Ativos</div>
              <div style={{fontSize:28,fontWeight:700,color:'var(--text-primary)',marginTop:6}}>{d.anuncios.ativos}</div>
              <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>{d.anuncios.pausados} pausados - {d.anuncios.total} total</div>
            </div>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,borderTop:'3px solid var(--green)'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5}}>Vendas no Mes</div>
              <div style={{fontSize:28,fontWeight:700,color:'var(--green)',marginTop:6}}>{rv.total_vendas}</div>
              <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Ticket medio: {fmt(rv.ticket_medio)}</div>
            </div>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,borderTop:'3px solid #f59e0b'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5}}>Faturamento ML</div>
              <div style={{fontSize:28,fontWeight:700,color:'var(--text-primary)',marginTop:6}}>{fmt(rv.faturamento)}</div>
              <div style={{fontSize:12,color:'var(--red)',marginTop:4}}>Taxas ML: {fmt(rv.total_taxas)}</div>
            </div>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,borderTop:'3px solid var(--red)'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5}}>Perguntas Pendentes</div>
              <div style={{fontSize:28,fontWeight:700,color:parseInt(d.perguntas.pendentes)>0?'var(--red)':'var(--green)',marginTop:6}}>{d.perguntas.pendentes}</div>
              <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>{d.perguntas.total} total recebidas</div>
            </div>
          </div>
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
            <h3 style={{fontSize:15,fontWeight:700,marginBottom:16,color:'var(--text-primary)'}}>Ultimas Vendas</h3>
            {vendas.slice(0,5).map(v=>(
              <div key={v.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:'1px solid var(--border-light)'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:'var(--text-primary)'}}>{v.titulo_produto}</div>
                  <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>{v.comprador_nickname} - {new Date(v.data_pedido).toLocaleDateString('pt-BR')}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:15,fontWeight:700,color:'var(--green)'}}>{fmt(v.valor_total)}</div>
                  <span style={{fontSize:11,padding:'3px 8px',borderRadius:6,fontWeight:600,background:v.status_pedido==='paid'?'var(--green-bg)':v.status_pedido==='cancelled'?'var(--red-bg)':'var(--amber-bg)',color:v.status_pedido==='paid'?'var(--green-text)':v.status_pedido==='cancelled'?'var(--red-text)':'var(--amber-text)'}}>{v.status_pedido}</span>
                </div>
              </div>
            ))}
            {vendas.length===0&&<div style={{textAlign:'center',padding:20,color:'var(--text-muted)',fontSize:13}}>Nenhuma venda no periodo</div>}
          </div>
        </div>
      )}

      {tab==='anuncios'&&(
        <div>
          <div style={{display:'flex',gap:10,marginBottom:16}}>
            <button onClick={()=>setModalCriar(true)} style={{padding:'10px 18px',background:'var(--green)',color:'white',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>+ Novo Anuncio</button>
            <input placeholder="Buscar anuncio..."  value={buscaAnuncio} onChange={e=>setBuscaAnuncio(e.target.value)} style={{flex:1,padding:'10px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:13}} />
            <select value={filtroAnuncio} onChange={e=>setFiltroAnuncio(e.target.value)} style={{padding:'10px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:13}}>
              <option value="all">Todos</option><option value="active">Ativos</option><option value="paused">Pausados</option><option value="closed">Fechados</option>
            </select>
          </div>
          <div style={{display:'grid',gap:12}}>
            {anuncios.map(a=>(
              <div key={a.id} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,display:'flex',gap:16,alignItems:'center',transition:'all .2s'}}>
                <img src={a.thumbnail?.replace('http:','https:')} alt="" style={{width:70,height:70,borderRadius:10,objectFit:'cover',background:'var(--bg-primary)'}} />
                <div style={{flex:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:600,color:'var(--text-primary)',marginBottom:4}}>{a.titulo}</div>
                      <div style={{fontSize:12,color:'var(--text-muted)'}}>{a.ml_item_id} - {a.vendidos} vendidos - Estoque: {a.estoque_disponivel}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:18,fontWeight:700,color:'var(--green)'}}>{fmt(a.preco)}</div>
                      <span style={{fontSize:11,padding:'3px 8px',borderRadius:6,fontWeight:600,background:a.status==='active'?'var(--green-bg)':a.status==='paused'?'var(--amber-bg)':'var(--red-bg)',color:a.status==='active'?'var(--green-text)':a.status==='paused'?'var(--amber-text)':'var(--red-text)'}}>{a.status}</span>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,marginTop:10}}>
                    <a href={a.permalink} target="_blank" rel="noreferrer" style={{padding:'5px 12px',borderRadius:6,border:'1px solid var(--border)',fontSize:12,color:'var(--text-secondary)',textDecoration:'none',fontWeight:500}}>Ver no ML</a>
                    {a.status==='active'&&<button onClick={()=>pausarAnuncio(a.ml_item_id)} style={{padding:'5px 12px',borderRadius:6,border:'1px solid var(--amber)',fontSize:12,color:'var(--amber)',background:'transparent',cursor:'pointer',fontWeight:500,fontFamily:'inherit'}}>Pausar</button>}
                    {a.status==='paused'&&<button onClick={()=>reativarAnuncio(a.ml_item_id)} style={{padding:'5px 12px',borderRadius:6,border:'1px solid var(--green)',fontSize:12,color:'var(--green)',background:'transparent',cursor:'pointer',fontWeight:500,fontFamily:'inherit'}}>Reativar</button>}
                    <button onClick={()=>setModalAnuncio(a)} style={{padding:'5px 12px',borderRadius:6,border:'1px solid var(--accent)',fontSize:12,color:'var(--accent)',background:'transparent',cursor:'pointer',fontWeight:500,fontFamily:'inherit'}}>Detalhes</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==='vendas'&&(
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:20}}>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:18,textAlign:'center'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase'}}>Total Vendas</div>
              <div style={{fontSize:24,fontWeight:700,color:'var(--text-primary)',marginTop:6}}>{rv.total_vendas}</div>
            </div>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:18,textAlign:'center'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase'}}>Faturamento</div>
              <div style={{fontSize:24,fontWeight:700,color:'var(--green)',marginTop:6}}>{fmt(rv.faturamento)}</div>
            </div>
            <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:18,textAlign:'center'}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase'}}>Taxas ML</div>
              <div style={{fontSize:24,fontWeight:700,color:'var(--red)',marginTop:6}}>{fmt(rv.total_taxas)}</div>
            </div>
          </div>
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--bg-primary)',borderBottom:'1px solid var(--border)'}}>
                <th style={{padding:'12px 16px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Produto</th>
                <th style={{padding:'12px 16px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Comprador</th>
                <th style={{padding:'12px 16px',textAlign:'center',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Qtd</th>
                <th style={{padding:'12px 16px',textAlign:'right',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Valor</th>
                <th style={{padding:'12px 16px',textAlign:'center',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Status</th>
                <th style={{padding:'12px 16px',textAlign:'right',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase'}}>Data</th>
              </tr></thead>
              <tbody>
                {vendas.map(v=>(
                  <tr key={v.id} style={{borderBottom:'1px solid var(--border-light)'}}>
                    <td style={{padding:'12px 16px',fontWeight:500,color:'var(--text-primary)',maxWidth:250,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v.titulo_produto}</td>
                    <td style={{padding:'12px 16px',color:'var(--text-secondary)'}}>{v.comprador_nickname}</td>
                    <td style={{padding:'12px 16px',textAlign:'center',color:'var(--text-primary)',fontWeight:600}}>{v.quantidade}</td>
                    <td style={{padding:'12px 16px',textAlign:'right',fontWeight:700,color:'var(--green)'}}>{fmt(v.valor_total)}</td>
                    <td style={{padding:'12px 16px',textAlign:'center'}}><span style={{fontSize:11,padding:'3px 8px',borderRadius:6,fontWeight:600,background:v.status_pedido==='paid'?'var(--green-bg)':'var(--amber-bg)',color:v.status_pedido==='paid'?'var(--green-text)':'var(--amber-text)'}}>{v.status_pedido}</span></td>
                    <td style={{padding:'12px 16px',textAlign:'right',color:'var(--text-muted)',fontSize:12}}>{new Date(v.data_pedido).toLocaleDateString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {vendas.length===0&&<div style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>Nenhuma venda no periodo</div>}
          </div>
        </div>
      )}

      {tab==='perguntas'&&(
        <div style={{display:'grid',gap:12}}>
          {perguntas.map(p=>(
            <div key={p.id} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,borderLeft:p.status==='UNANSWERED'?'3px solid var(--amber)':'3px solid var(--green)'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>{p.ml_item_id} - {new Date(p.data_pergunta).toLocaleDateString('pt-BR')}</span>
                <span style={{fontSize:11,padding:'3px 8px',borderRadius:6,fontWeight:600,background:p.status==='UNANSWERED'?'var(--amber-bg)':'var(--green-bg)',color:p.status==='UNANSWERED'?'var(--amber-text)':'var(--green-text)'}}>{p.status==='UNANSWERED'?'Pendente':'Respondida'}</span>
              </div>
              <div style={{fontSize:14,fontWeight:500,color:'var(--text-primary)',marginBottom:6}}>"{p.pergunta}"</div>
              {p.resposta&&<div style={{fontSize:13,color:'var(--text-secondary)',background:'var(--bg-primary)',padding:10,borderRadius:8,marginTop:8}}>Resposta: {p.resposta}</div>}
              {p.status==='UNANSWERED'&&<button onClick={()=>{setModalResposta(p);setResposta('');}} style={{marginTop:10,padding:'7px 14px',background:'var(--accent)',color:'white',border:'none',borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Responder</button>}
            </div>
          ))}
          {perguntas.length===0&&<div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Nenhuma pergunta</div>}
        </div>
      )}

      {modalAnuncio&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={()=>setModalAnuncio(null)}>
          <div style={{background:'var(--bg-card)',borderRadius:14,width:'90%',maxWidth:560,maxHeight:'85vh',overflowY:'auto',border:'1px solid var(--border)',boxShadow:'0 25px 50px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid var(--border)',background:'var(--bg-primary)',borderRadius:'14px 14px 0 0'}}>
              <h2 style={{fontSize:17,fontWeight:700,color:'var(--text-primary)',margin:0}}>Detalhes do Anuncio</h2>
              <button onClick={()=>setModalAnuncio(null)} style={{background:'var(--bg-card)',border:'1px solid var(--border)',color:'var(--text-muted)',fontSize:18,width:32,height:32,borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>X</button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <div style={{textAlign:'center',marginBottom:16}}><img src={modalAnuncio.thumbnail?.replace('http:','https:')} alt="" style={{width:120,height:120,borderRadius:12,objectFit:'cover'}} /></div>
              {[['Titulo',modalAnuncio.titulo],['ID',modalAnuncio.ml_item_id],['Preco',fmt(modalAnuncio.preco)],['Status',modalAnuncio.status],['Estoque',modalAnuncio.estoque_disponivel+' / '+modalAnuncio.estoque_total],['Vendidos',modalAnuncio.vendidos],['Tipo',modalAnuncio.tipo_publicacao],['Saude',modalAnuncio.saude_anuncio?Math.round(modalAnuncio.saude_anuncio*100)+'%':'N/A'],['Criado',new Date(modalAnuncio.data_criacao).toLocaleDateString('pt-BR')]].map(([l,v],i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',marginBottom:6,background:'var(--bg-primary)',borderRadius:8,borderLeft:'3px solid var(--accent)'}}>
                  <span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:13}}>{l}:</span>
                  <span style={{fontSize:13,color:'var(--text-primary)'}}>{v}</span>
                </div>
              ))}
              <a href={modalAnuncio.permalink} target="_blank" rel="noreferrer" style={{display:'block',textAlign:'center',marginTop:16,padding:'10px 20px',background:'#ffe600',color:'#333',borderRadius:8,textDecoration:'none',fontWeight:700,fontSize:14}}>Abrir no Mercado Livre</a>
            </div>
          </div>
        </div>
      )}

      {modalResposta&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={()=>setModalResposta(null)}>
          <div style={{background:'var(--bg-card)',borderRadius:14,width:'90%',maxWidth:500,border:'1px solid var(--border)',boxShadow:'0 25px 50px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',background:'var(--bg-primary)',borderRadius:'14px 14px 0 0'}}>
              <h2 style={{fontSize:17,fontWeight:700,color:'var(--text-primary)',margin:0}}>Responder Pergunta</h2>
            </div>
            <div style={{padding:'20px 24px'}}>
              <div style={{background:'var(--bg-primary)',padding:14,borderRadius:8,marginBottom:16,borderLeft:'3px solid var(--amber)'}}>
                <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:4}}>{modalResposta.ml_item_id}</div>
                <div style={{fontSize:14,color:'var(--text-primary)',fontWeight:500}}>"{modalResposta.pergunta}"</div>
              </div>
              <textarea value={resposta} onChange={e=>setResposta(e.target.value)} placeholder="Digite sua resposta..." rows={4} style={{width:'100%',padding:12,borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14,resize:'vertical'}} />
              <div style={{display:'flex',gap:10,marginTop:14,justifyContent:'flex-end'}}>
                <button onClick={()=>setModalResposta(null)} style={{padding:'9px 18px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-secondary)',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:500}}>Cancelar</button>
                <button onClick={()=>responderPergunta(modalResposta.pergunta_id)} style={{padding:'9px 18px',borderRadius:8,border:'none',background:'var(--accent)',color:'white',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:600}}>Enviar Resposta</button>
              </div>
            </div>
          </div>
        </div>
      )}
    
      {modalCriar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,overflow:'auto',padding:20}} onClick={()=>setModalCriar(false)}>
          <div style={{background:'var(--bg-card)',borderRadius:14,width:'95%',maxWidth:640,maxHeight:'90vh',overflowY:'auto',border:'1px solid var(--border)',boxShadow:'0 25px 50px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid var(--border)',background:'var(--bg-primary)',borderRadius:'14px 14px 0 0',position:'sticky',top:0,zIndex:1}}>
              <h2 style={{fontSize:17,fontWeight:700,color:'var(--text-primary)',margin:0}}>Novo Anuncio - Mercado Livre</h2>
              <button onClick={()=>setModalCriar(false)} style={{background:'var(--bg-card)',border:'1px solid var(--border)',color:'var(--text-muted)',fontSize:18,width:32,height:32,borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>X</button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:0.5}}>Titulo do Anuncio *</label>
                <input value={novoAnuncio.titulo} onChange={e=>{setNovoAnuncio(p=>({...p,titulo:e.target.value}));if(e.target.value.length>=5)buscarCategorias(e.target.value);}} placeholder="Ex: Camera de Seguranca Hikvision 2MP Full HD" style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14}} />
              </div>

              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:0.5}}>Categoria * {novoAnuncio.categoria_nome&&<span style={{color:'var(--green)',textTransform:'none'}}>({novoAnuncio.categoria_nome})</span>}</label>
                {buscandoCat&&<div style={{fontSize:12,color:'var(--text-muted)',marginBottom:6}}>Buscando categorias...</div>}
                {categoriaSugestoes.length>0&&(
                  <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:10}}>
                    {categoriaSugestoes.slice(0,6).map((c,i)=>(
                      <button key={i} onClick={()=>selecionarCategoria(c)} style={{padding:'6px 12px',borderRadius:6,border:'1px solid var(--border)',background:novoAnuncio.categoria_id===c.category_id?'var(--accent)':'var(--bg-primary)',color:novoAnuncio.categoria_id===c.category_id?'white':'var(--text-secondary)',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>{c.category_name||c.domain_name}</button>
                    ))}
                  </div>
                )}
                {!novoAnuncio.categoria_id&&<div style={{fontSize:12,color:'var(--amber)'}}>Digite o titulo acima para sugerir categorias automaticamente</div>}
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16}}>
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Preco (R$) *</label>
                  <input type="number" step="0.01" value={novoAnuncio.preco} onChange={e=>setNovoAnuncio(p=>({...p,preco:e.target.value}))} placeholder="29.90" style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14}} />
                </div>
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Estoque *</label>
                  <input type="number" value={novoAnuncio.estoque} onChange={e=>setNovoAnuncio(p=>({...p,estoque:e.target.value}))} style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14}} />
                </div>
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Condicao</label>
                  <select value={novoAnuncio.condicao} onChange={e=>setNovoAnuncio(p=>({...p,condicao:e.target.value}))} style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14}}>
                    <option value="new">Novo</option><option value="used">Usado</option>
                  </select>
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Tipo de Publicacao</label>
                <select value={novoAnuncio.tipo_publicacao} onChange={e=>setNovoAnuncio(p=>({...p,tipo_publicacao:e.target.value}))} style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14}}>
                  <option value="gold_pro">Premium (Full)</option><option value="gold_special">Classico</option><option value="gold">Ouro</option><option value="free">Gratis</option>
                </select>
              </div>

              {atributosCategoria.length>0&&(
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:10,textTransform:'uppercase'}}>Atributos Obrigatorios</label>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    {atributosCategoria.map((a,i)=>(
                      <div key={i}>
                        <label style={{display:'block',fontSize:11,color:'var(--text-secondary)',marginBottom:4}}>{a.name}</label>
                        {a.values&&a.values.length>0?(
                          <select value={novoAnuncio.atributos[i]?.value_name||''} onChange={e=>atualizarAtributo(i,e.target.value)} style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:13}}>
                            <option value="">Selecione...</option>
                            {a.values.map((v,j)=><option key={j} value={v.name}>{v.name}</option>)}
                          </select>
                        ):(
                          <input value={novoAnuncio.atributos[i]?.value_name||''} onChange={e=>atualizarAtributo(i,e.target.value)} placeholder={a.name} style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:13}} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Fotos do Produto</label>
                {fotosUpload.length>0&&(
                  <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:12}}>
                    {fotosUpload.map((f,i)=>(
                      <div key={i} style={{position:'relative',width:80,height:80,borderRadius:10,overflow:'hidden',border:'2px solid var(--green)'}}>
                        <img src={f.url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                        <button onClick={()=>removerFoto(i)} style={{position:'absolute',top:2,right:2,width:20,height:20,borderRadius:'50%',background:'var(--red)',color:'white',border:'none',fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>x</button>
                      </div>
                    ))}
                  </div>
                )}
                <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'14px 20px',borderRadius:10,border:'2px dashed var(--border)',background:'var(--bg-primary)',cursor:uploading?'not-allowed':'pointer',opacity:uploading?0.6:1,transition:'all .2s'}}>
                  <input type="file" accept="image/*" multiple onChange={e=>{Array.from(e.target.files).forEach(f=>uploadFoto(f));e.target.value='';}} style={{display:'none'}} disabled={uploading} />
                  <span style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500}}>{uploading?'Enviando...':'Clique para selecionar fotos ou arraste aqui'}</span>
                </label>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6}}>JPG, PNG ate 10MB. Minimo 1 foto recomendada.</div>
              </div>

              <div style={{marginBottom:20}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase'}}>Descricao</label>
                <textarea value={novoAnuncio.descricao} onChange={e=>setNovoAnuncio(p=>({...p,descricao:e.target.value}))} rows={4} placeholder="Descricao detalhada do produto..." style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'inherit',fontSize:14,resize:'vertical'}} />
              </div>

              <div style={{display:'flex',gap:10,justifyContent:'flex-end',paddingTop:16,borderTop:'1px solid var(--border)'}}>
                <button onClick={()=>setModalCriar(false)} style={{padding:'11px 20px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-primary)',color:'var(--text-secondary)',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:500}}>Cancelar</button>
                <button onClick={criarAnuncio} disabled={criando} style={{padding:'11px 24px',borderRadius:8,border:'none',background:'var(--green)',color:'white',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700,opacity:criando?0.6:1}}>{criando?'Criando...':'Criar Anuncio'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default Marketplace;
